import React, { useState } from "react";
import { Sparkles, Loader2, Copy, CalendarClock, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useLang, translateFr as tfr } from "@/i18n";
import { negCard, fmtTermValue, NEG_STATUS_FR } from "./negotiationShared";
import { downloadPdf } from "./negotiationEvents";

export function NegotiationBriefs({ neg }) {
  const { tf, locale } = useLang();
  const [busyKind, setBusyKind] = useState(null);
  const [brief, setBrief] = useState(null);

  const generate = async (kind) => {
    setBusyKind(kind);
    try {
      const r = await api.post(`/negotiations/${neg.id}/briefs`, { kind });
      setBrief(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
    finally { setBusyKind(null); }
  };

  return (
    <div className={cn(negCard, "p-4 space-y-2")} data-testid="neg-briefs">
      <h3 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-foreground/85 flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5 text-red-500" /> Athlynx Intelligence
      </h3>
      <Button variant="outline" size="sm" className="w-full h-9 justify-start" disabled={!!busyKind} data-testid="neg-brief-summary-btn"
        onClick={() => generate("summary")}>
        {busyKind === "summary" ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-2" />}
        {tf("Synthèse de négociation")}
      </Button>
      <Button variant="outline" size="sm" className="w-full h-9 justify-start" disabled={!!busyKind} data-testid="neg-brief-meeting-btn"
        onClick={() => generate("meeting_prep")}>
        {busyKind === "meeting_prep" ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <CalendarClock className="h-3.5 w-3.5 mr-2" />}
        {tf("Préparer la réunion")}
      </Button>
      <p className="text-[10px] text-muted-foreground">{tf("Généré uniquement à partir des données validées de la négociation.")}</p>

      <BriefDialog brief={brief} neg={neg} onClose={() => setBrief(null)} tf={tf} locale={locale} />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h4 className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground mb-1">{title}</h4>
      {children}
    </div>
  );
}

function TermList({ rows, locale, tf, empty }) {
  if (!rows?.length) return <p className="text-xs text-muted-foreground">{empty}</p>;
  return (
    <ul className="space-y-0.5 text-sm">
      {rows.map((r) => (
        <li key={r.term_key || r.label} className="flex justify-between gap-3">
          <span>{tf(r.label)}</span>
          <span className="font-medium text-right">{fmtTermValue(r.vtype, r.currency, r.value, locale, tf)}</span>
        </li>
      ))}
    </ul>
  );
}

function BriefDialog({ brief, neg, onClose, tf, locale }) {
  const [exporting, setExporting] = useState(false);
  if (!brief) return null;
  const s = brief.sections || {};
  const ai = brief.ai;
  const fmtMoney = (o) => (o ? fmtTermValue("money", o.currency, o.amount, locale, tf) : "—");

  const exportPdf = async () => {
    setExporting(true);
    try {
      await downloadPdf(api, `/negotiations/${neg.id}/briefs/${brief.id}/pdf`, {},
        `Athlynx_${brief.kind === "meeting_prep" ? "Meeting_Brief" : "Negotiation_Summary"}.pdf`);
      toast.success(tf("PDF téléchargé"));
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); }
    finally { setExporting(false); }
  };

  const copyText = () => {
    const lines = [
      `${tf(brief.kind === "summary" ? "Synthèse de négociation" : "Préparation de réunion")} — ${s.athlete || ""} / ${s.counterparty || ""}`,
      `${tf("Version")} V${brief.version_number || "—"} · ${new Date(brief.generated_at).toLocaleString(locale)}`,
      ai?.headline ? `\n${ai.headline}` : "",
      ai?.key_points?.length ? `\n${tf("Points clés")}:\n` + ai.key_points.map((p) => `• ${p}`).join("\n") : "",
      `\n${tf("Points acquis")}:\n` + (s.agreed || []).map((r) => `• ${tf(r.label)}: ${fmtTermValue(r.vtype, r.currency, r.value, locale, tf)}`).join("\n"),
      `\n${tf("Points ouverts")}:\n` + (s.open || []).map((r) => `• ${tf(r.label)}: ${fmtTermValue(r.vtype, r.currency, r.value, locale, tf)}`).join("\n"),
      ai?.priorities?.length ? `\n${tf("Priorités")}:\n` + ai.priorities.map((p) => `• ${p}`).join("\n") : "",
      ai?.questions_to_clarify?.length ? `\n${tf("Questions à clarifier")}:\n` + ai.questions_to_clarify.map((p) => `• ${p}`).join("\n") : "",
      ai?.suggested_next_actions?.length ? `\n${tf("Prochaines actions")}:\n` + ai.suggested_next_actions.map((p) => `• ${p}`).join("\n") : "",
    ].filter(Boolean).join("\n");
    navigator.clipboard?.writeText(lines).then(() => toast.success(tf("Copié dans le presse-papiers")));
  };

  return (
    <Dialog open={!!brief} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="neg-brief-dialog">
        <DialogTitle>{tf(brief.kind === "summary" ? "Synthèse de négociation" : "Préparation de réunion")}</DialogTitle>
        <DialogDescription data-testid="neg-brief-meta">
          {s.athlete || "—"} · {s.counterparty || "—"} · {brief.version_number ? `V${brief.version_number}` : tf("Aucune version finalisée")} · {new Date(brief.generated_at).toLocaleString(locale)}
        </DialogDescription>

        {!brief.ai_available && (
          <p className="text-[11px] text-amber-500 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2" data-testid="neg-brief-ai-unavailable">
            {tf("Athlynx Intelligence est momentanément indisponible — synthèse basée uniquement sur les données structurées.")}
          </p>
        )}

        <div className="space-y-4">
          {ai && (
            <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-red-500" data-testid="neg-brief-ai-tag">
              {tf("Recommandations Athlynx Intelligence")} — {tf("à confirmer par l'agent")}
            </p>
          )}
          {ai?.headline && <p className="text-sm font-medium" data-testid="neg-brief-headline">{ai.headline}</p>}
          {ai?.key_points?.length > 0 && (
            <Section title={tf("Points clés")}>
              <ul className="space-y-1 text-sm list-disc pl-4">{ai.key_points.map((p, i) => <li key={i}>{p}</li>)}</ul>
            </Section>
          )}
          <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground pt-1" data-testid="neg-brief-facts-tag">{tf("Faits vérifiés")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {[[tfr("Statut"), tf(NEG_STATUS_FR[s.status] || String(s.status || "—"))], [tfr("Version actuelle"), s.current_version ? `V${s.current_version}` : "—"],
              ["Proposition actuelle", fmtMoney(s.current_proposal)], ["Notre position", fmtMoney(s.our_position)]].map(([l, v]) => (
              <div key={l} className="flex justify-between gap-2"><span className="text-muted-foreground">{tf(l)}</span><span className="font-medium">{v}</span></div>
            ))}
          </div>
          <Section title={`${tf("Points acquis")} (${(s.agreed || []).length})`}>
            <TermList rows={s.agreed} locale={locale} tf={tf} empty={tf("Aucun point acquis pour le moment.")} />
          </Section>
          <Section title={`${tf("Points ouverts")} (${(s.open || []).length})`}>
            <TermList rows={s.open} locale={locale} tf={tf} empty={tf("Aucun point ouvert.")} />
          </Section>
          {(s.needs_clarification || []).length > 0 && (
            <Section title={tf("À clarifier")}>
              <TermList rows={s.needs_clarification} locale={locale} tf={tf} empty="" />
            </Section>
          )}
          {((s.gaps || []).length > 0 || (s.recent_changes || []).length > 0 || (s.deadlines || []).length > 0) && (
            <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground pt-1" data-testid="neg-brief-signals-tag">{tf("Signaux calculés")}</p>
          )}
          {(s.gaps || []).length > 0 && (
            <Section title={tf("Écart restant vs objectifs")}>
              <ul className="space-y-0.5 text-sm">
                {s.gaps.map((g) => (
                  <li key={g.term_key} className="flex justify-between gap-3">
                    <span>{tf(g.label)}</span>
                    <span className="font-medium">{fmtTermValue("money", g.currency, g.gap_remaining, locale, tf)}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}
          {(s.recent_changes || []).length > 0 && (
            <Section title={tf("Derniers mouvements")}>
              <ul className="space-y-0.5 text-sm">
                {s.recent_changes.map((c, i) => (
                  <li key={i}>{tf(c.label)} : <span className="text-muted-foreground">{c.before ?? "—"}</span> → <span className="font-medium">{c.after ?? "—"}</span></li>
                ))}
              </ul>
            </Section>
          )}
          {(s.deadlines || []).length > 0 && (
            <Section title={tf("Échéances")}>
              <ul className="space-y-0.5 text-sm">
                {s.deadlines.map((d, i) => <li key={i} className="flex justify-between gap-3"><span>{tf(d.label)}</span><span className="font-medium">{new Date(d.date).toLocaleDateString(locale)}</span></li>)}
              </ul>
            </Section>
          )}
          {ai?.priorities?.length > 0 && (
            <Section title={tf("Priorités")}>
              <ul className="space-y-1 text-sm list-disc pl-4">{ai.priorities.map((p, i) => <li key={i}>{p}</li>)}</ul>
            </Section>
          )}
          {ai?.talking_points?.length > 0 && (
            <Section title={tf("Arguments pour la réunion")}>
              <ul className="space-y-1 text-sm list-disc pl-4">{ai.talking_points.map((p, i) => <li key={i}>{p}</li>)}</ul>
            </Section>
          )}
          {ai?.questions_to_clarify?.length > 0 && (
            <Section title={tf("Questions à clarifier")}>
              <ul className="space-y-1 text-sm list-disc pl-4">{ai.questions_to_clarify.map((p, i) => <li key={i}>{p}</li>)}</ul>
            </Section>
          )}
          {ai?.suggested_next_actions?.length > 0 && (
            <Section title={tf("Prochaines actions")}>
              <ul className="space-y-1 text-sm list-disc pl-4">{ai.suggested_next_actions.map((p, i) => <li key={i}>{p}</li>)}</ul>
            </Section>
          )}
          {s.next_action?.label && (
            <Section title={tf("Prochaine action enregistrée")}>
              <p className="text-sm font-medium">{s.next_action.label}{s.next_action.due_date ? ` · ${new Date(s.next_action.due_date).toLocaleDateString(locale)}` : ""}</p>
            </Section>
          )}
        </div>

        <div className="flex justify-between items-center pt-2">
          <p className="text-[10px] text-muted-foreground">{tf("Généré par")} {brief.generated_by}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="h-9" disabled={exporting} onClick={exportPdf} data-testid="neg-brief-pdf-btn">
              {exporting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5 mr-1.5" />} {tf("Exporter en PDF")}
            </Button>
            <Button variant="outline" size="sm" className="h-9" onClick={copyText} data-testid="neg-brief-copy-btn">
              <Copy className="h-3.5 w-3.5 mr-1.5" /> {tf("Copier")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
